// 登录 / 注册页：沿用策略咖的编辑感视觉，同时保留现有 Supabase 认证逻辑。
// 051 F13：补齐「忘记密码」闭环——登录页新增重置入口（resetPasswordForEmail 邮件链路），
// 邮件链接携带 ?recovery=1 回到本页 → recovery 会话下展示「设置新密码」表单 → updateUser。
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { supabase } from "../lib/supabase";
import { BrandLogo } from "../components/BrandLogo";
import "./Login.css";

type Flow = "auth" | "forgot" | "reset";

/** 邮件重置链接的回跳地址（须在 Supabase Auth Redirect URLs 白名单内） */
function recoveryRedirectTo(): string {
  return `${window.location.origin}/login?recovery=1`;
}

export default function Login() {
  const { user, loading, signIn, signUp } = useAuth();
  const nav = useNavigate();
  // 邮件重置链接回跳（/login?recovery=1#access_token=...&type=recovery）：
  // supabase-js 自动从 hash 建立 recovery 会话，本页据此进入「设置新密码」表单
  const [flow, setFlow] = useState<Flow>(() =>
    new URLSearchParams(window.location.search).get("recovery") === "1" ? "reset" : "auth"
  );
  const [mode, setMode] = useState<"in" | "up">("in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // recovery 流程不下自动跳转：必须先完成「设置新密码」
    if (user && flow !== "reset") nav("/projects", { replace: true });
  }, [user, flow, nav]);

  // recovery 会话未建立（链接无效/过期/已使用）→ 如实告知并提供重发入口
  const [resetLinkBroken, setResetLinkBroken] = useState(false);
  useEffect(() => {
    if (flow === "reset" && !loading && !user) setResetLinkBroken(true);
  }, [flow, loading, user]);

  function backToAuth() {
    setFlow("auth"); setErr(null); setMsg(null); setPassword(""); setConfirm("");
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (flow === "auth" && mode === "in") {
        await signIn(email, password);
        nav("/projects");
      } else if (flow === "auth") {
        await signUp(email, password, name || email.split("@")[0]);
        setMsg("注册成功！请查收验证邮件后登录。"); setMode("in");
      } else if (flow === "forgot") {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: recoveryRedirectTo(),
        });
        if (error) throw error;
        setMsg(`重置邮件已发送至 ${email}，请按邮件内链接设置新密码（链接 24 小时内有效）。若未收到请检查垃圾邮件。`);
      } else {
        if (password !== confirm) { setErr("两次输入的新密码不一致。"); return; }
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        nav("/projects", { replace: true });
      }
    } catch (error) { setErr((error as Error).message); }
    finally { setBusy(false); }
  }

  const title = flow === "forgot" ? "重置密码"
    : flow === "reset" ? "设置新密码"
    : mode === "in" ? "欢迎回来" : "创建你的工作空间";
  const subtitle = flow === "forgot" ? "输入注册邮箱，我们会向你发送密码重置链接。"
    : flow === "reset" ? "身份已通过邮件链接验证，请为账号设置新密码。"
    : mode === "in" ? "继续你的下一套营销方案。" : "注册后即可开始第一份方案。";

  return (
    <div className="aim-login">
      <header className="aim-login-header"><Link className="aim-login-wordmark" to="/" aria-label="返回策略咖首页"><BrandLogo /></Link><Link className="aim-login-back" to="/">返回首页 <span aria-hidden="true">↗</span></Link></header>
      <main className="aim-login-main">
        <section className="aim-login-intro" aria-labelledby="login-title"><p className="aim-login-eyebrow">策略咖 / WORKSPACE</p><h1 id="login-title">从一份简报，<br />开始下一次<em>增长。</em></h1><p className="aim-login-lede">进入你的方案空间，继续编辑已有提案，或创建一套新的营销计划。</p><div className="aim-login-signal" aria-hidden="true"><div className="aim-login-signal-frame frame-one" /><div className="aim-login-signal-frame frame-two" /><div className="aim-login-signal-core"><span>READY</span><strong>01</strong></div></div><div className="aim-login-note"><strong>你的资料，只用来完成你的方案</strong><span /></div></section>
        <section className="aim-login-panel" aria-label={flow === "auth" ? (mode === "in" ? "登录表单" : "注册表单") : "密码重置表单"}>
          <div className="aim-login-panel-top"><p className="aim-login-eyebrow">YOUR WORKSPACE</p><p className="aim-login-status"><i /> SECURE ACCESS</p></div>
          {flow === "auth" && <div className="aim-login-tabs" role="tablist" aria-label="登录或注册"><button className={mode === "in" ? "is-active" : ""} onClick={() => { setMode("in"); setErr(null); setMsg(null); }} type="button" role="tab" aria-selected={mode === "in"}>登录</button><button className={mode === "up" ? "is-active" : ""} onClick={() => { setMode("up"); setErr(null); setMsg(null); }} type="button" role="tab" aria-selected={mode === "up"}>注册</button></div>}
          <form onSubmit={submit} className="aim-login-form"><div className="aim-login-form-title"><h2>{title}</h2><p>{subtitle}</p></div>
            {flow === "auth" && mode === "up" && <label>昵称<input value={name} onChange={(e) => setName(e.target.value)} placeholder="怎么称呼您" autoComplete="name" /></label>}
            {flow !== "reset" && <label>邮箱<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" /></label>}
            {flow === "auth" && <label>密码<input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 位" autoComplete={mode === "in" ? "current-password" : "new-password"} /></label>}
            {flow === "auth" && mode === "in" && <div className="aim-login-forgot-row"><button type="button" className="aim-login-forgot" onClick={() => { setFlow("forgot"); setErr(null); setMsg(null); }}>忘记密码？</button></div>}
            {flow === "reset" && !resetLinkBroken && <>
              <label>新密码<input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="至少 6 位" autoComplete="new-password" /></label>
              <label>确认新密码<input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="再输入一次" autoComplete="new-password" /></label>
            </>}
            {flow === "reset" && resetLinkBroken && <div className="aim-login-link-broken"><p className="aim-login-message is-error" role="alert">重置链接无效或已过期，请重新申请。</p><button type="button" className="aim-login-forgot" onClick={() => { setFlow("forgot"); setResetLinkBroken(false); setErr(null); }}>重新发送重置邮件</button></div>}
            {err && <p className="aim-login-message is-error" role="alert">{err}</p>}
            {msg && <p className="aim-login-message is-success" role="status">{msg}</p>}
            {!(flow === "reset" && resetLinkBroken) && <button className="aim-login-submit" disabled={busy}>{busy ? "处理中…" : flow === "auth" ? (mode === "in" ? "进入工作空间" : "创建账号") : flow === "forgot" ? "发送重置邮件" : "更新密码并进入工作空间"}<span aria-hidden="true">↗</span></button>}
            {flow !== "auth" && <p className="aim-login-alt"><button type="button" className="aim-login-forgot" onClick={backToAuth}>← 返回登录</button></p>}
          </form>
          <p className="aim-login-footnote">继续即表示你同意我们的 <Link to="/security">数据安全承诺</Link>。</p>
        </section>
      </main>
    </div>
  );
}
